<template>
  <div class="max-w-5xl mx-auto py-8 px-4 md:px-8 md:mt-16 bg-white">
    <!-- Header -->
    <header
      class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6"
    >
      <div class="text-start">
        <h1 class="text-5xl font-semibold tracking-wider uppercase">
          Avaliação Final
        </h1>
        <p class="text-lg text-gray-400 tracking-wide">
          Responda todas as questões para habilitar o envio da tentativa.
        </p>
      </div>

      <!-- Progresso -->
      <div class="w-full md:w-80">
        <div class="flex justify-between text-base mb-1 tracking-wider">
          <span>{{ answeredCount }} / {{ totalQuestions }} respondidas</span>
          <span>{{ progressPct }}%</span>
        </div>
        <div class="h-2 w-full bg-gray-700/70 rounded-full overflow-hidden">
          <div
            class="h-full bg-emerald-500 rounded-full transition-all duration-300"
            :style="{ width: progressPct + '%' }"
          ></div>
        </div>
      </div>
    </header>

    <!-- Lista de perguntas -->
    <section class="space-y-5">
      <article
        v-for="(q, idx) in questions"
        :key="q.id"
        class="rounded-xl border border-gray-700 bg-neutral-200 p-4 md:p-5"
      >
        <div class="mb-3">
          <h2 v-if="q.prompt" class="font-semibold tracking-wider text-3xl">
            {{ q.prompt }}
          </h2>
          <p class="mt-1 text-base tracking-wider">
            {{ idx + 1 }}. {{ q.title || "Pergunta" }}
          </p>
        </div>

        <!-- Opções (uma correta) -->
        <div class="grid gap-3 text-2xl">
          <label
            v-for="opt in q.options"
            :key="opt.id"
            class="flex items-start gap-3 rounded-lg border border-gray-700 bg-neutral-300 p-3 cursor-pointer hover:border-gray-500 transition"
            :class="{
              'ring-2 ring-emerald-500/60 border-emerald-600/60':
                answers[q.id] === opt.id,
            }"
          >
            <input
              class="mt-1 h-4 w-4 shrink-0 accent-emerald-500"
              type="radio"
              :name="`q-${q.id}`"
              :value="opt.id"
              v-model="answers[q.id]"
              @change="onAnyAnswer()"
            />
            <div class="leading-relaxed tracking-wider">
              {{ opt.text }}
            </div>
          </label>
        </div>
      </article>
    </section>

    <!-- Barra de envio (fixa no final da página) -->
    <div class="h-24"></div>
    <footer class="bottom-4 left-0 right-0">
      <div class="max-w-5xl mx-auto px-4">
        <div
          class="flex flex-col md:flex-row md:items-center gap-3 rounded-xl border border-gray-700 bg-neutral-200 p-3 backdrop-blur tracking-wide"
        >
          <div class="text-base tracking-wider">
            <strong>{{ answeredCount }}</strong> de
            {{ totalQuestions }} respostas marcadas
          </div>
          <div class="ml-auto flex gap-2">
            <button
              type="button"
              class="px-4 py-2 rounded-lg border border-gray-700 hover:bg-gray-400 uppercase cursor-pointer"
              @click="confirmLeave()"
            >
              sair
            </button>
            <button
              type="button"
              class="px-5 py-2 uppercase rounded-lg tracking-wide text-base focus:outline-none text-white font-bold bg-[#d20a0a] border-2 border-red-700 hover:bg-white hover:text-red-700 transition-colors duration-300 disabled:hover:text-gray-300 disabled:text-gray-400 disabled:bg-[#222222] disabled:border-2 disabled:border-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
              :disabled="!allAnswered"
              @click="submitAttempt"
            >
              finalizar tentativa
            </button>
          </div>
        </div>
      </div>
    </footer>

    <!-- Modal de confirmação de saída -->
    <div
      v-if="showLeaveModal"
      class="fixed inset-0 z-50 flex items-center justify-center"
      aria-modal="true"
      role="dialog"
      :key="showLeaveModal"
    >
      <div class="absolute inset-0 bg-black/60" @click="cancelLeave"></div>
      <div
        class="relative z-10 w-full max-w-md mx-4 rounded-xl border border-gray-700 bg-neutral-900 p-5"
      >
        <h3 class="text-white font-semibold text-3xl tracking-wider">
          Deseja sair desta página?
        </h3>
        <p class="text-gray-300 mt-2 tracking-wide text-lg">
          Sua tentativa poderá ser contabilizada. Tem certeza que deseja sair
          agora?
        </p>
        <div class="mt-5 flex justify-end gap-2">
          <button
            class="px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800 font-bold tracking-wide uppercase"
            @click="cancelLeave"
          >
            Cancelar
          </button>
          <button
            class="px-4 py-2 rounded-lg text-base focus:outline-none uppercase text-white font-bold tracking-wide bg-[#d20a0a] border-2 border-red-700 hover:bg-white hover:text-red-700 transition-colors duration-300 disabled:hover:text-gray-300 disabled:text-gray-400 disabled:bg-[#222222] disabled:border-2 disabled:border-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
            @click="confirmLeaveProceed"
          >
            Sair mesmo assim
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { showToast as toast } from "@/helpers/UseToasts";
import { dataAssessment } from "@/shared/store/assessment";
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";

const route = useRoute();
const router = useRouter();
const questions = computed(() => dataAssessment.getters["questions"]);
const attemptId = computed(() => dataAssessment.getters["attemptId"]);
const lastAttemptId = computed(() => dataAssessment.getters["lastAttemptId"]);
const answers = ref({}); // { [questionId]: optionId }
const submitted = ref(false)

// Proteção contra saída sem confirmar (SPA + refresh)
const attemptStarted = ref(false);
const showLeaveModal = ref(false);
let nextRouteResolver = null;

onMounted(() => {
  if (!attemptId.value || questions.value.length === 0) {
    router.replace({
      name: "avaliable-info",
      params: { id: route.params.id },
    });
  }
});

const totalQuestions = computed(() => questions.value.length);
const allAnswered = computed(
  () =>
    totalQuestions.value > 0 &&
    Object.keys(answers.value).length === totalQuestions.value
);
const answeredCount = computed(
  () =>
    Object.keys(answers.value).filter((qid) => answers.value[qid] != null)
      .length
);
const progressPct = computed(() => {
  if (!totalQuestions.value) return 0;
  return Math.round((answeredCount.value / totalQuestions.value) * 100);
});

function onAnyAnswer() {
  attemptStarted.value = true;
}

async function submitAttempt() {
  if (!allAnswered.value) return;
  submitted.value = true

  try {
    await dataAssessment.dispatch("submitAllAnswers", answers.value)
    await dataAssessment.dispatch("finalizeAttempt");

    // redireciona p/ resultado
    router.push({
      name: "avaliable-result",
      params: { 
        attemptId: lastAttemptId.value,
        tId: route.params.id
      }
    });
  } catch(err) {
    if (err?.response?.data?.errors && err.response.data?.message) {
      router.push({ name: "home" });
      return toast("Aviso", err.response.data?.message, "error");
    }

    router.push({ name: "home" });
    return toast("Aviso", 'Algo deu errado ao busar prova', "error");
  };
}

// SPA: intercepta troca de rota
onBeforeRouteLeave((to, from, next) => {
  if (!attemptStarted.value || submitted.value) return next();
  showLeaveModal.value = true;
  nextRouteResolver = next; // guardamos para decidir depois
});

// Modal ações
function cancelLeave() {
  showLeaveModal.value = false;
  if (nextRouteResolver) {
    nextRouteResolver(false);
    nextRouteResolver = null;
  }
}

// atalho do botão "Sair" no footer
function confirmLeave() {
  if (!attemptStarted.value || submitted.value) 
    return router.back()
    // return router.back();
  showLeaveModal.value = true;
}

function confirmLeaveProceed() {
  // libera a navegação que estava bloqueada
  cleanupBeforeUnload();
  const resolver = nextRouteResolver;
  nextRouteResolver = null;
  showLeaveModal.value = false;
  if (resolver) return resolver();
  
  // abandonou
  Promise.all([
    dataAssessment.dispatch("finalizeAttempt", { abandoned: true })
  ])

  // se não veio de navegação, apenas voltar
  router.push({
    name: "avaliable-info",
    params: { 
      id: route.params.id 
    }
  });
}

// Navegador/refresh: usa beforeunload nativo
function beforeUnload(e) {
  if (attemptStarted.value && !submitted.value) {
    e.preventDefault();
    e.returnValue = ""; // necessário para o prompt nativo
    return "";
  }
}
function setupBeforeUnload() {
  window.addEventListener("beforeunload", beforeUnload);
}

function cleanupBeforeUnload() {
  window.removeEventListener("beforeunload", beforeUnload);
}

onMounted(setupBeforeUnload);
onBeforeUnmount(cleanupBeforeUnload);
</script>

<style scoped>
/* Sem estilos obrigatórios além do Tailwind */
</style>
